/**
 * Sender Key management for E2E encrypted media.
 *
 * Each participant generates an AES-256-GCM Sender Key and distributes it
 * pairwise to every peer via ECDH-derived shared secrets. Epochs track
 * key rotation; old keys are retained briefly for in-flight frames.
 */

import {
  SENDER_KEY_LENGTH,
  SENDER_KEY_GRACE_PERIOD_MS,
  PREV_EPOCH_EXPIRY_MS,
} from '../constants'
import { participantIdToKeyId } from './identity'
import type { SenderKeyPair } from '../types'

// ============================================================
// Types
// ============================================================

/** Tracked key with epoch and expiry metadata */
interface EpochKey {
  epoch: number
  key: CryptoKey
  rawKey: ArrayBuffer
  keyId: number
  createdAt: number
  expiresAt: number | null // null = current (no expiry)
}

/** Per-peer key store: participantId -> list of epoch keys (current + grace) */
const peerKeys = new Map<string, EpochKey[]>()

/** Local sender key state */
let localEpoch = 0
let localKey: EpochKey | null = null

// ============================================================
// Key Generation
// ============================================================

/**
 * Generate a fresh AES-256-GCM sender key.
 * Returns both the CryptoKey and raw bytes for distribution.
 */
export async function generateSenderKey(): Promise<SenderKeyPair> {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: SENDER_KEY_LENGTH },
    true, // extractable for pairwise distribution
    ['encrypt', 'decrypt'],
  )

  const rawKey = await crypto.subtle.exportKey('raw', key)

  localEpoch++
  const keyId = localEpoch // simple incrementing ID

  return { keyId, key, rawKey }
}

/**
 * Generate and install a new local sender key.
 * Returns the SenderKeyPair for distribution to peers.
 */
export async function rotateLocalKey(
  localParticipantId: string,
): Promise<SenderKeyPair> {
  const pair = await generateSenderKey()

  // Expire old local key with grace period
  if (localKey) {
    localKey.expiresAt = Date.now() + SENDER_KEY_GRACE_PERIOD_MS
  }

  const epochKey: EpochKey = {
    epoch: localEpoch,
    key: pair.key,
    rawKey: pair.rawKey,
    keyId: pair.keyId,
    createdAt: Date.now(),
    expiresAt: null, // current key
  }

  localKey = epochKey
  installPeerKey(localParticipantId, epochKey)

  return pair
}

// ============================================================
// ECDH Pairwise Distribution
// ============================================================

/**
 * Derive a shared AES-GCM wrapping key from local ECDH private + remote ECDH public.
 * Used to encrypt sender keys before sending over the DataChannel.
 */
export async function deriveSharedSecret(
  localPrivateKey: CryptoKey,
  remotePublicKey: CryptoKey,
): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: 'ECDH', public: remotePublicKey },
    localPrivateKey,
    { name: 'AES-GCM', length: 256 },
    false, // not extractable
    ['encrypt', 'decrypt'],
  )
}

/**
 * Encrypt a sender key for pairwise distribution using the ECDH shared secret.
 * Returns IV + ciphertext as a single ArrayBuffer.
 */
export async function encryptSenderKey(
  sharedSecret: CryptoKey,
  senderKeyRaw: ArrayBuffer,
): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    sharedSecret,
    senderKeyRaw,
  )

  // Concatenate: iv (12 bytes) + ciphertext
  const result = new Uint8Array(12 + ciphertext.byteLength)
  result.set(iv, 0)
  result.set(new Uint8Array(ciphertext), 12)
  return result.buffer
}

/**
 * Decrypt a sender key received from a peer using the ECDH shared secret.
 * Expects IV (12 bytes) + ciphertext format.
 */
export async function decryptSenderKey(
  sharedSecret: CryptoKey,
  encryptedData: ArrayBuffer,
): Promise<ArrayBuffer> {
  const data = new Uint8Array(encryptedData)
  const iv = data.slice(0, 12)
  const ciphertext = data.slice(12)

  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    sharedSecret,
    ciphertext,
  )
}

// ============================================================
// Peer Key Storage
// ============================================================

/**
 * Install a peer's sender key (after decrypting it).
 * Old keys for the same peer are kept until they expire.
 */
function installPeerKey(participantId: string, epochKey: EpochKey): void {
  const existing = peerKeys.get(participantId) ?? []
  existing.push(epochKey)
  peerKeys.set(participantId, existing)
}

/**
 * Store a received peer sender key.
 * Called after decrypting the key from a sender-key-distribute or sender-key-rotate message.
 */
export async function installReceivedKey(
  participantId: string,
  epoch: number,
  rawKey: ArrayBuffer,
  prevEpochExpiryMs: number = PREV_EPOCH_EXPIRY_MS,
): Promise<void> {
  const key = await crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM', length: SENDER_KEY_LENGTH },
    false,
    ['encrypt', 'decrypt'],
  )

  const keyId = participantIdToKeyId(participantId)

  // Expire previous keys for this peer
  const existing = peerKeys.get(participantId) ?? []
  for (const ek of existing) {
    if (ek.expiresAt === null) {
      ek.expiresAt = Date.now() + prevEpochExpiryMs
    }
  }

  installPeerKey(participantId, {
    epoch,
    key,
    rawKey,
    keyId,
    createdAt: Date.now(),
    expiresAt: null, // current
  })
}

/**
 * Get the current (non-expired) sender key for a peer.
 * Used for decrypting incoming frames.
 */
export function getPeerKey(participantId: string): EpochKey | null {
  const keys = peerKeys.get(participantId)
  if (!keys) return null

  const now = Date.now()
  // Prefer current (no expiry), fallback to most recent non-expired
  for (let i = keys.length - 1; i >= 0; i--) {
    const k = keys[i]!
    if (k.expiresAt === null || k.expiresAt > now) {
      return k
    }
  }
  return null
}

/**
 * Get the local sender key for encrypting outgoing frames.
 */
export function getLocalKey(): EpochKey | null {
  return localKey
}

/**
 * Get the current local epoch number.
 */
export function getLocalEpoch(): number {
  return localEpoch
}

// ============================================================
// Key Rotation
// ============================================================

/**
 * Remove all keys for a peer who left.
 * Their old keys remain briefly for in-flight frame decryption.
 */
export function expirePeerKeys(participantId: string): void {
  const keys = peerKeys.get(participantId)
  if (!keys) return

  const now = Date.now()
  for (const k of keys) {
    if (k.expiresAt === null) {
      k.expiresAt = now + SENDER_KEY_GRACE_PERIOD_MS
    }
  }
}

/**
 * Garbage-collect expired keys across all peers.
 * Call periodically (e.g. on heartbeat).
 */
export function purgeExpiredKeys(): void {
  const now = Date.now()
  for (const [participantId, keys] of peerKeys) {
    const active = keys.filter(
      (k) => k.expiresAt === null || k.expiresAt > now,
    )
    if (active.length === 0) {
      peerKeys.delete(participantId)
    } else {
      peerKeys.set(participantId, active)
    }
  }
}

/**
 * Remove a peer entirely (after grace period for their keys).
 */
export function removePeer(participantId: string): void {
  peerKeys.delete(participantId)
}

/**
 * Clear all key state (for leaving a meeting).
 */
export function clearAllKeys(): void {
  peerKeys.clear()
  localKey = null
  localEpoch = 0
}
